import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Board from "../components/Board";
import BoardFullscreenShell from "../components/BoardFullscreenShell";
import GameEndModal from "../components/GameEndModal";
import GameChatBar from "../components/GameChatBar";
import InviteModal from "../components/InviteModal";
import { ScorePanel } from "../components/ScorePanel";
import Clock from "../components/Clock";
import { toast } from "sonner";
import api, { fetchWsToken, formatApiError, wsUrl } from "../lib/api";
import { getLegalMoves, playerHasCapture } from "../game/engine";
import { Flag, Loader2, Link as LinkIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { displayName } from "../lib/displayName";

export default function GameOnline() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [you, setYou] = useState(null);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const wsRef = useRef(null);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [animatingMove, setAnimatingMove] = useState(null);
  const prevWinnerRef = useRef(null);
  const prevMovesRef = useRef(0);
  const hadOpponentRef = useRef(false);
  const userId = user?.id;

  useEffect(() => {
    if (!state?.players || !user?.id) return;
    for (const [color, p] of Object.entries(state.players)) {
      if (p?.id === user.id) {
        setYou(color);
        return;
      }
    }
  }, [state?.players, user?.id]);

  useEffect(() => {
    if (prevWinnerRef.current && !state?.winner) {
      toast.success("Revanche lancée !");
      setSelected(null);
      prevMovesRef.current = 0;
    }
    prevWinnerRef.current = state?.winner ?? null;
  }, [state?.winner]);

  useEffect(() => {
    if (!state?.players?.B || hadOpponentRef.current) return;
    if (you === "A" && state.players.B?.name) {
      toast.success(`${state.players.B.name} a rejoint la partie !`);
    }
    hadOpponentRef.current = true;
  }, [state?.players?.B, you]);

  useEffect(() => {
    if (!roomId || !userId) return;
    let cancelled = false;
    let ws = null;
    let pingId = null;
    let pollId = null;
    let reconnectTimer = null;
    let reconnectAttempt = 0;
    let wsOpen = false;

    const applyRoomState = (roomState) => {
      if (cancelled || !roomState) return;
      setState(roomState);
      setConnecting(false);
    };

    const fetchRoomState = async () => {
      try {
        const { data } = await api.get(`/games/online/${roomId}`);
        applyRoomState(data);
        return data;
      } catch {
        return null;
      }
    };

    const openWebSocket = async (knownColor) => {
      if (cancelled) return;
      let wsToken = "";
      try {
        wsToken = await fetchWsToken();
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e) || "Session expirée. Reconnectez-vous.");
          setConnecting(false);
        }
        return;
      }
      if (cancelled) return;

      ws = new WebSocket(wsUrl(roomId, wsToken));
      wsRef.current = ws;

      ws.onopen = () => {
        wsOpen = true;
        reconnectAttempt = 0;
        if (!cancelled) {
          setConnecting(false);
          setError(null);
        }
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "state") {
            setState((prev) => {
              const newState = msg.state;
              if (prev && newState.moves > prevMovesRef.current && msg.state.board) {
                /* animation handled locally */
              }
              prevMovesRef.current = newState.moves;
              return newState;
            });
            if (msg.you) setYou(msg.you);
            else if (knownColor) setYou(knownColor);
            if (!cancelled) {
              setError(null);
              setConnecting(false);
            }
          } else if (msg.type === "chat") {
            setState((s) => s ? { ...s, chat: [...(s.chat || []), msg.entry] } : s);
          } else if (msg.type === "error") {
            const detail = msg.detail || "Erreur";
            if (!cancelled) setError(detail);
            toast.error(detail);
          }
        } catch (_) { /* ignore */ }
      };

      ws.onerror = () => {
        if (!cancelled) {
          setError("Connexion temps réel interrompue. Nouvelle tentative…");
        }
      };

      ws.onclose = (ev) => {
        wsOpen = false;
        if (cancelled) return;
        setConnecting(false);
        if (ev.code !== 1000 && reconnectAttempt < 8) {
          reconnectAttempt += 1;
          reconnectTimer = setTimeout(() => {
            openWebSocket(knownColor);
          }, Math.min(1500 * reconnectAttempt, 6000));
        } else if (ev.code !== 1000) {
          setError((prev) => prev || "Connexion au serveur de jeu interrompue.");
        }
      };
    };

    const connect = async () => {
      hadOpponentRef.current = false;
      setConnecting(true);
      setError(null);
      let myColor = null;
      try {
        const { data } = await api.post(`/games/online/join/${roomId}`);
        myColor = data?.color || null;
        if (myColor) setYou(myColor);
      } catch (e) {
        const status = e.response?.status;
        const detail = typeof e.response?.data?.detail === "string" ? e.response.data.detail : "";
        if (status === 404) {
          setError("Partie introuvable. La salle a peut-être expiré — créez-en une nouvelle.");
          setConnecting(false);
          return;
        }
        if (status === 400 && detail.includes("complète")) {
          setError("La partie est complète.");
          setConnecting(false);
          return;
        }
        if (status === 400 && detail.includes("terminée")) {
          setError("Cette partie est déjà terminée.");
          setConnecting(false);
          return;
        }
        if (status === 401) {
          setError("Connectez-vous pour rejoindre cette partie.");
          setConnecting(false);
          return;
        }
        setError(formatApiError(e) || "Impossible de rejoindre la partie.");
        setConnecting(false);
        return;
      }

      if (cancelled) return;

      await fetchRoomState();

      const ping = () => api.post("/games/online/presence").catch(() => {});
      ping();
      pingId = setInterval(ping, 60000);

      pollId = setInterval(async () => {
        const data = await fetchRoomState();
        if (data?.players?.B && wsOpen) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 2500);

      await openWebSocket(myColor);
    };

    connect();

    return () => {
      cancelled = true;
      if (pingId) clearInterval(pingId);
      if (pollId) clearInterval(pollId);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch (_) {}
    };
  }, [roomId, userId]);

  const board = state?.board;
  const turn = state?.turn;
  const winner = state?.winner;

  const allLegal = useMemo(() => {
    if (!board || !you || winner) return [];
    if (turn !== you) return [];
    return getLegalMoves(board, you);
  }, [board, you, turn, winner]);

  const mustCapturePieces = useMemo(() => {
    if (!board || !you || winner) return new Set();
    if (turn !== you) return new Set();
    if (!playerHasCapture(board, you)) return new Set();
    const out = new Set();
    for (const m of allLegal) if (m.captures.length) out.add(`${m.from[0]},${m.from[1]}`);
    return out;
  }, [board, you, turn, winner, allLegal]);

  const legalForSelected = useMemo(() => {
    if (!selected) return [];
    return allLegal.filter((m) => m.from[0] === selected[0] && m.from[1] === selected[1]);
  }, [allLegal, selected]);

  useEffect(() => {
    if (!state || !state.turn_started_at || state.winner) return;
    const cur = state.turn;
    const left = state.time_left?.[cur] ?? 0;
    const startMs = new Date(state.turn_started_at).getTime();
    const expiresAt = startMs + left * 1000;
    const ms = expiresAt - Date.now();
    if (ms <= 0) {
      wsRef.current?.send(JSON.stringify({ type: "claim_timeout" }));
      return;
    }
    const id = setTimeout(() => {
      wsRef.current?.send(JSON.stringify({ type: "claim_timeout" }));
    }, ms + 200);
    return () => clearTimeout(id);
  }, [state?.turn, state?.turn_started_at, state?.winner, state?.time_left, state]);

  const onClickIntersection = (x, y) => {
    if (!board || !you || winner) return;
    if (turn !== you) {
      toast.info("Ce n'est pas votre tour.");
      return;
    }
    if (animatingMove) return;
    const piece = board[y][x];
    if (piece && piece.owner === you) {
      if (mustCapturePieces.size > 0 && !mustCapturePieces.has(`${x},${y}`)) {
        toast.warning("Capture obligatoire.");
        return;
      }
      setSelected([x, y]);
      return;
    }
    if (selected) {
      const m = legalForSelected.find((mv) => mv.to[0] === x && mv.to[1] === y);
      if (!m) return;
      const movingPiece = board[selected[1]][selected[0]];
      setAnimatingMove({
        path: m.path,
        owner: movingPiece.owner,
        king: movingPiece.king,
        duration: 600 + m.captures.length * 200,
        capturedCells: m.captures,
        onDone: () => {
          setAnimatingMove(null);
          wsRef.current?.send(JSON.stringify({ type: "move", move: { from: m.from, to: m.to, captures: m.captures } }));
        },
      });
      setSelected(null);
    }
  };

  const sendChat = (text) => {
    if (!text || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: "chat", text }));
  };

  const reportMessage = async (payload) => {
    try {
      await api.post(`/games/online/${roomId}/chat/report`, payload);
      toast.success("Message signalé. Merci.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'envoyer le signalement.");
    }
  };

  const resign = () => {
    if (!wsRef.current || winner) return;
    if (!window.confirm("Abandonner la partie ?")) return;
    wsRef.current.send(JSON.stringify({ type: "resign" }));
  };

  const requestRematch = () => {
    wsRef.current?.send(JSON.stringify({ type: "rematch_request" }));
  };

  const acceptRematch = () => {
    wsRef.current?.send(JSON.stringify({ type: "rematch_request" }));
  };

  const declineRematch = () => {
    wsRef.current?.send(JSON.stringify({ type: "rematch_decline" }));
  };

  const inviteLink = `${window.location.origin}/game/online/${roomId}`;

  const waitingForOpponent = state && (!state.players?.A || !state.players?.B);
  const opponentColor = you === "A" ? "B" : "A";
  const opponentName = (() => {
    if (!state || !you) return "";
    return state.players?.[opponentColor]?.name || "En attente…";
  })();
  const opponentUserId = state?.players?.[opponentColor]?.id;

  const rematchInfo = useMemo(() => {
    if (!you || !state?.rematch) return { you: false, opponent: false };
    const other = you === "A" ? "B" : "A";
    return {
      you: !!state.rematch[you],
      opponent: !!state.rematch[other],
    };
  }, [you, state?.rematch]);

  const prevRematchOpponentRef = useRef(false);
  useEffect(() => {
    if (!winner || !rematchInfo.opponent || rematchInfo.you) {
      if (!winner) prevRematchOpponentRef.current = false;
      return;
    }
    if (!prevRematchOpponentRef.current) {
      toast.info(`${opponentName} souhaite une revanche !`);
    }
    prevRematchOpponentRef.current = rematchInfo.opponent;
  }, [winner, rematchInfo, opponentName]);

  const chatDisabled = connecting || !!error || waitingForOpponent;

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 fade-up">
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-xs tracking-[0.3em] text-[color:var(--primary)]">MATCH EN LIGNE</p>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="online-title">
                {you ? (you === "A" ? "Vous : Blancs" : "Vous : Noirs") : "Connexion…"}
              </h1>
              <p className="text-xs text-[color:var(--text-secondary)]">
                Adversaire : <span data-testid="online-opponent-name">{opponentName}</span>
              </p>
            </div>
            <div className="flex gap-2">
              {waitingForOpponent && (
                <button onClick={() => setInviteOpen(true)} className="px-3 py-2 rounded-md border border-[color:var(--primary)]/50 bg-[color:var(--primary)]/10 text-xs flex items-center gap-2 hover:bg-[color:var(--primary)]/20" data-testid="online-invite">
                  <LinkIcon size={14} /> Inviter
                </button>
              )}
              <button onClick={resign} className="px-3 py-2 rounded-md border border-[color:var(--border)] text-xs flex items-center gap-2 hover:bg-[color:var(--surface)]" data-testid="online-resign">
                <Flag size={14} /> Abandonner
              </button>
              <button onClick={() => navigate("/lobby")} className="px-3 py-2 rounded-md border border-[color:var(--border)] text-xs hover:bg-[color:var(--surface)]" data-testid="online-back">
                Quitter
              </button>
            </div>
          </div>

          {connecting && !state && (
            <div className="glass rounded-lg p-6 text-center text-sm flex items-center justify-center gap-2" data-testid="online-connecting">
              <Loader2 className="animate-spin" size={16}/> Connexion en cours…
            </div>
          )}
          {connecting && state && (
            <p className="text-xs text-center text-[color:var(--text-secondary)] mb-3 flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={12} /> Synchronisation…
            </p>
          )}
          {error && (
            <div className="glass rounded-lg p-6 text-center text-sm text-rose-400" data-testid="online-error">{error}</div>
          )}

          {state && (
            <>
              <BoardFullscreenShell>
                <div className="flex items-center justify-between max-w-[560px] mx-auto mb-3">
                  <Clock
                    label={`BLANCS ${you === "A" ? "· VOUS" : ""}`}
                    seconds={state.time_left?.A ?? state.time_limit ?? 300}
                    active={turn === "A" && !winner && !waitingForOpponent}
                    turnStartedAt={state.turn_started_at}
                    testid="clock-A"
                  />
                  <div className="text-[10px] tracking-[0.3em] text-[color:var(--text-secondary)]">VS</div>
                  <Clock
                    label={`NOIRS ${you === "B" ? "· VOUS" : ""}`}
                    seconds={state.time_left?.B ?? state.time_limit ?? 300}
                    active={turn === "B" && !winner && !waitingForOpponent}
                    turnStartedAt={state.turn_started_at}
                    testid="clock-B"
                  />
                </div>

                <ScorePanel
                  turn={turn}
                  winner={winner}
                  captures={state.captures || { A: 0, B: 0 }}
                  you={you}
                />
                {waitingForOpponent && (
                  <div className="glass rounded-lg p-4 mb-3 text-center text-sm" data-testid="online-waiting">
                    En attente d&apos;un adversaire…
                    <button onClick={() => setInviteOpen(true)} className="mt-2 mx-auto block px-4 py-2 rounded-md btn-gold text-xs" data-testid="online-waiting-invite">
                      Inviter un joueur
                    </button>
                  </div>
                )}
                <Board
                  board={board}
                  selectedFrom={selected}
                  legalMovesForSelected={legalForSelected}
                  mustCapturePieces={mustCapturePieces}
                  onClickIntersection={onClickIntersection}
                  flipped={you === "B"}
                  animatingMove={animatingMove}
                  capturedCells={animatingMove?.capturedCells || []}
                  hintsOn={false}
                />
                <GameEndModal
                  open={!!winner && !!you && !waitingForOpponent}
                  mode="online"
                  youWon={winner === you}
                  winnerSide={winner}
                  opponentName={opponentName}
                  rematch={rematchInfo}
                  onRematch={requestRematch}
                  onAcceptRematch={acceptRematch}
                  onDeclineRematch={declineRematch}
                  onLeave={() => navigate("/lobby")}
                />
              </BoardFullscreenShell>

              {!waitingForOpponent && (
                <GameChatBar
                  you={you}
                  messages={state.chat || []}
                  opponentUserId={opponentUserId}
                  onSend={sendChat}
                  onReport={reportMessage}
                  disabled={chatDisabled}
                />
              )}
            </>
          )}
        </section>
      </main>

      <InviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        inviteLink={inviteLink}
        roomId={roomId}
        hostName={displayName(user)}
      />
    </div>
  );
}
